import { ApiError } from '@/lib/api';
import {
  EXPORT_PRESET_NAME_MAX,
  MAX_EXPORT_PRESETS_PER_USER,
  exportPresetNameSchema,
  normalizePresetSettings,
} from '@mss_creator/shared';
import type {
  ExportPresetSettings,
  SaveExportPresetInput,
  UserExportPreset,
} from '@mss_creator/shared';
import { Button, TextField } from '@pms-digital/design-system';
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

interface ExportPresetSaveFormProps {
  /** The current options; the form sends them with the arrangement fields cleared. */
  options: ExportPresetSettings;
  presets: readonly UserExportPreset[];
  /** Pre-filled when the last applied preset was a personal one: the "update my preset" flow. */
  initialName: string;
  save: (input: SaveExportPresetInput) => Promise<UserExportPreset>;
  isSaving: boolean;
  /**
   * The presets list failed to load: the overwrite warning and the cap check
   * cannot work without it, so saving is off until it loads.
   */
  listUnavailable: boolean;
  onSaved: (saved: UserExportPreset) => void;
  onCancel: () => void;
}

const CAP_MESSAGE = `${MAX_EXPORT_PRESETS_PER_USER} presets maximum — supprimez-en un`;
const UNAVAILABLE_MESSAGE = "Enregistrement impossible : vos presets n'ont pas pu être chargés.";

/**
 * « Enregistrer comme preset… » (issue #13, spec P3): an inline disclosure,
 * not a modal: one short form beside the options it saves. Re-saving an
 * existing name overwrites it, so the form warns first (the field's help
 * line) and its button reads « Remplacer ». Enter submits, Escape cancels.
 */
export function ExportPresetSaveForm({
  options,
  presets,
  initialName,
  save,
  isSaving,
  listUnavailable,
  onSaved,
  onCancel,
}: ExportPresetSaveFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  // Focus on open without `autoFocus`, which the a11y lint forbids.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const overwrites = presets.some((preset) => preset.name === trimmed);
  const atCap = !overwrites && presets.length >= MAX_EXPORT_PRESETS_PER_USER;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = exportPresetNameSchema.safeParse(name);
    if (!parsed.success) {
      setError(
        trimmed === ''
          ? 'Donnez un nom au preset.'
          : 'Ce nom contient des caractères non autorisés.',
      );
      return;
    }
    if (atCap || listUnavailable) return;
    setError(null);
    try {
      const saved = await save({ name: parsed.data, settings: normalizePresetSettings(options) });
      onSaved(saved);
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 409
          ? CAP_MESSAGE
          : 'Enregistrement impossible — réessayez.',
      );
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  }

  const message = error ?? (listUnavailable ? UNAVAILABLE_MESSAGE : atCap ? CAP_MESSAGE : null);
  return (
    <form
      aria-label="Enregistrer comme preset"
      onSubmit={(event) => void handleSubmit(event)}
      onKeyDown={handleKeyDown}
      className="space-y-2 rounded-md border-2 border-gray-200 bg-white p-3"
    >
      <TextField
        ref={inputRef}
        label="Nom du preset"
        value={name}
        maxLength={EXPORT_PRESET_NAME_MAX}
        status={message !== null ? 'error' : 'default'}
        message={message ?? undefined}
        help={overwrites ? 'Un preset porte déjà ce nom : il sera remplacé.' : undefined}
        onChange={(event) => {
          setName(event.target.value);
          setError(null);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Annuler
        </Button>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          disabled={isSaving || atCap || listUnavailable}
        >
          {overwrites ? 'Remplacer' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  );
}
